import { TaxReturnSummaryFormSideOverPage } from '../TaxReturnSummaryFormSideOverPage';
import { IconButton, Modal, useSideOverContext } from '../../components';
import { useRevalidator } from '@remix-run/react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  TaxReturnSummaryFormSubmitData,
  updateTaxReturnSummaryServiceInputFromForm,
  useDeleteTaxReturnSummary,
  useTaxReturnSummary,
  useTaxReturnSummaryForm,
  useUpdateTaxReturnSummaryService,
} from '../../../TaxReturnSummary';
import { useFeatureFlagsContext } from '../../../FeatureFlags';

function useEditTaxPage({ householdID, taxReturnSummaryID }: { householdID: string; taxReturnSummaryID: string }) {
  const { revalidate } = useRevalidator();
  const { t: tTaxes } = useTranslation('taxes');
  const { closeSideOver } = useSideOverContext();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { allowDocuments } = useFeatureFlagsContext();
  const { deleteTaxReturnSummary } = useDeleteTaxReturnSummary();
  const { updateTaxReturnSummaryService } = useUpdateTaxReturnSummaryService();
  const { taxReturnSummary } = useTaxReturnSummary({ householdID, taxReturnSummaryID });

  function deleteTaxReturnSummaryFn() {
    deleteTaxReturnSummary.mutate({ householdID, id: taxReturnSummaryID }, { onSuccess: () => closeSideOver() });
  }

  function handleUpdateSubmit(data: TaxReturnSummaryFormSubmitData) {
    updateTaxReturnSummaryService.mutate(updateTaxReturnSummaryServiceInputFromForm(data), {
      onSuccess: () => {
        closeSideOver();
        // We revalidate to ensure no remix-side data is left behind
        revalidate();
      },
    });
  }

  const useTaxReturnSummaryFormReturn = useTaxReturnSummaryForm({
    householdID,
    handleSubmit: handleUpdateSubmit,
    taxReturnSummaryID: taxReturnSummaryID,
  });

  const isDeleteLoading = deleteTaxReturnSummary.isLoading;
  const isUpdating = updateTaxReturnSummaryService.isLoading;

  return {
    tTaxes,
    isUpdating,
    deleteOpen,
    householdID,
    setDeleteOpen,
    allowDocuments,
    isDeleteLoading,
    taxReturnSummary,
    deleteTaxReturnSummaryFn,
    useTaxReturnSummaryFormReturn,
  };
}

export const EditTaxPage = ({
  householdID,
  exitRoute,
  taxReturnSummaryID,
  showNotes = false,
}: {
  householdID: string;
  exitRoute: string;
  taxReturnSummaryID: string;
  showNotes?: boolean;
}) => {
  const {
    tTaxes,
    deleteOpen,
    isUpdating,
    setDeleteOpen,
    isDeleteLoading,
    allowDocuments,
    taxReturnSummary,
    deleteTaxReturnSummaryFn,
    useTaxReturnSummaryFormReturn,
  } = useEditTaxPage({ householdID, taxReturnSummaryID });

  return (
    <>
      <TaxReturnSummaryFormSideOverPage
        isEdit={true}
        exit={exitRoute}
        isLoading={isUpdating}
        allowDocuments={!!allowDocuments}
        showNotes={showNotes}
        useTaxReturnSummaryFormReturn={useTaxReturnSummaryFormReturn}
        sideOverHeaderChildren={
          <IconButton
            diameter={40}
            icon="delete"
            theme="danger"
            onClick={() => setDeleteOpen(true)}
            className="sm:mb-[10px] sm:mr-[34px] ml-auto"
          />
        }
      />
      <Modal
        isOpen={deleteOpen}
        okThemeType="danger"
        okLoading={isDeleteLoading}
        onOk={deleteTaxReturnSummaryFn}
        okText={tTaxes('delete-taxes')}
        cancel={() => setDeleteOpen(false)}
        bodyText={tTaxes('delete-taxes-body')}
        title={tTaxes('delete-taxes-for', { for: taxReturnSummary?.stringifiedYear })}
        className="fixed inset-x-0 md:inset-x-auto bottom-4 sm:bottom-16 m-auto md:right-16 z-50 w-[calc(100vw_-_32px)] sm:!w-[542px]"
      />
    </>
  );
};
